// WHERE THE QUOTE IS — the span the origin modal lights up.
//
// The backend hands us a `position`: a character offset into the material's
// EXTRACTED TEXT, computed by docs-model.quotePosition. It is authoritative
// about WHICH PLACE, and approximate about WHICH CHARACTER — the offset is
// recovered by walking a whitespace-FOLDED copy back onto the original, so a
// run of whitespace before the quote can leave it a character or two early.
// Measured against allvue_dq's twelve material origins, ten land exactly and
// two land one character short (both on a leading space).
//
// So position is a HINT, not the answer, and the order here is deliberate:
//
//   1. EXACT AT THE POSITION. The common case, and the only one that needs no
//      judgement: the raw substring at `position` is the quote.
//   2. THE NEAREST NORMALISED MATCH. Fold whitespace and typography (Word turns
//      every apostrophe into U+2019 — the same normalisation the backend
//      verifier uses) and search the whole text, keeping an index map so the
//      hit maps back to real offsets. Of the matches, take the one CLOSEST to
//      the position: a phrase repeated three times in a document must highlight
//      the occurrence the review actually read.
//   3. A PREFIX OF THE QUOTE. A quote whose tail was edited since the review
//      still has a findable head; landing the reader on the right sentence with
//      part of it lit is better than landing them at the top of the file.
//   4. NOTHING. The caller says the words are not there any more, rather than
//      highlighting an arbitrary span and calling it provenance.
//
// Step 4 is why this returns null instead of falling back to `position`: an
// unfindable quote is a real signal about a material that has been edited, and
// a confident highlight over the wrong words is the one outcome worse than no
// highlight at all.

const FOLD = { "“": '"', "”": '"', "„": '"', "«": '"', "»": '"', "‘": "'", "’": "'", "`": "'", "´": "'", "–": "-", "—": "-" };
const foldChar = (c) => FOLD[c] || c.toLowerCase();
const TRIM = /^["'.…\s]+|["'.…\s]+$/g;

// Fold `text` the way the backend verifier does, and remember where every
// folded character came from — that map is what turns a match in the folded
// string back into a highlight in the real one.
function foldWithMap(text) {
  let out = "";
  const map = [];
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (/\s/.test(c)) {
      if (out.length && out[out.length - 1] === " ") continue;   // collapse the run
      out += " "; map.push(i);
    } else {
      out += foldChar(c); map.push(i);
    }
  }
  return { folded: out, map };
}

const foldQuote = (q) => String(q || "").split("").map((c) => (/\s/.test(c) ? " " : foldChar(c)))
  .join("").replace(/\s+/g, " ").trim().replace(TRIM, "");

// The needle is trimmed of the quote's own punctuation so it can be FOUND
// (the backend trims the same characters); the HIGHLIGHT should still cover
// them when the source carries them, or every quote ending in a full stop is
// lit one character short of its own sentence.
function widen(src, start, end, quote) {
  const tail = String(quote || "").match(/["'.…]+$/);
  if (tail) {
    const want = tail[0];
    if (src.slice(end, end + want.length) === want) return { start, end: end + want.length };
    // Word-authored text often carries just the stop, not the full run.
    if (src[end] && /["'.…]/.test(src[end])) return { start, end: end + 1 };
  }
  const head = String(quote || "").match(/^["'.…]+/);
  if (head && src.slice(Math.max(0, start - head[0].length), start) === head[0]) {
    return { start: start - head[0].length, end };
  }
  return { start, end };
}

/**
 * @returns {{start:number,end:number,exact:boolean,moved:boolean}|null}
 *   `moved` says the highlight is not where `position` claimed — the modal
 *   surfaces that rather than hiding a correction it made silently.
 */
export function locateQuote(text, quote, position) {
  const src = String(text || "");
  const q = String(quote || "").trim();
  const at = Number.isInteger(position) && position >= 0 && position < src.length ? position : null;
  if (!src) return null;

  // No quote to find: honour the position alone, as a zero-width caret.
  if (!q) return at == null ? null : { start: at, end: at, exact: false, moved: false };

  // 1 — exact at the position.
  if (at != null && src.slice(at, at + q.length) === q) {
    return { start: at, end: at + q.length, exact: true, moved: false };
  }

  const needle = foldQuote(q);
  if (!needle) return at == null ? null : { start: at, end: at, exact: false, moved: false };
  const { folded, map } = foldWithMap(src);
  const span = (i, len) => ({ start: map[i], end: map[Math.min(i + len - 1, map.length - 1)] + 1 });

  // 2 — the nearest normalised occurrence.
  let best = null;
  for (let from = 0; ; ) {
    const i = folded.indexOf(needle, from);
    if (i < 0) break;
    const s = span(i, needle.length);
    const d = at == null ? 0 : Math.abs(s.start - at);
    if (!best || d < best.d) best = { ...s, d };
    if (at == null) break;                       // no hint: the first hit is the answer
    from = i + 1;
  }
  if (best) {
    const w = widen(src, best.start, best.end, q);
    return { start: w.start, end: w.end, exact: false, moved: at != null && w.start !== at };
  }

  // 3 — a PREFIX of the quote. Cut it at a word boundary and shrink until it
  // matches: a fixed-length cut lands mid-word ("…queue for of|fline"), which
  // is exactly the half-word the edit is likely to have changed, so a single
  // fixed prefix fails on the case this fallback exists for.
  for (let len = Math.min(60, needle.length); len >= 12; ) {
    const cut = needle.slice(0, len).replace(/\s+\S*$/, "");   // back off to a whole word
    if (cut.length < 12) break;
    const i = folded.indexOf(cut);
    if (i >= 0) {
      const s = span(i, cut.length);
      return { start: s.start, end: s.end, exact: false, moved: at != null && s.start !== at };
    }
    len = cut.length - 1;
  }

  // 4 — the words are gone. Say so; do not invent a span.
  return null;
}

export default locateQuote;
